<template>
  <div class="supplier-profile">
    <!-- Header -->
    <div class="profile-header">
      <a-button type="link" icon="arrow-left" class="back-btn" @click="$router.push('/decaissement/fournisseurs')" />
      <span class="header-divider" />
      <h4 class="provider-name">{{ provider.fullName || "—" }}</h4>
      <a-tag v-if="provider.status === 'inactive'" color="red" class="status-tag">{{ $t("fournisseur.archived") }}</a-tag>
      <a-tag v-else color="green" class="status-tag">{{ $t("fournisseur.active") }}</a-tag>
      <div class="header-spacer" />
      <a-select
        size="small"
        style="width: 200px"
        show-search
        option-filter-prop="children"
        :value="provider._id"
        @change="handleProviderChange"
      >
        <a-select-option v-for="p in providersList" :key="p._id" :value="p._id">
          {{ p.fullName }}
        </a-select-option>
      </a-select>
      <a-button v-if="!editingMode" type="primary" size="small" icon="edit" @click="setEditingMode">
        {{ $t("action.modifier") }}
      </a-button>
      <template v-else>
        <a-button type="primary" size="small" :loading="loadingSave" icon="save" @click="saveProvider">
          {{ $t("action.enregistrer") }}
        </a-button>
        <a-button size="small" icon="close" @click="editingMode = false">
          {{ $t("action.annuler") }}
        </a-button>
      </template>
    </div>

    <a-alert
      v-if="provider.status === 'inactive'"
      type="warning"
      :message="$t('fournisseur.archivedNotice')"
      show-icon
      class="mb-3"
    />

    <!-- Main layout: left info + right insights -->
    <div class="profile-body">
      <div class="body-left">
        <!-- Contact & Business info -->
        <div class="card info-card">
          <div class="info-grid">
            <div class="info-section">
              <h5 class="section-title">
                <a-icon type="contacts" /> {{ $t("fournisseur.contactInfo") }}
              </h5>
              <a-form v-if="editingMode" :form="form" layout="vertical" class="compact-form">
                <a-form-item :label="$t('fournisseur.companyName')">
                  <a-input v-decorator="['fullName', { rules: [{ required: true, message: $t('requis.nom') }] }]" />
                </a-form-item>
                <a-form-item :label="$t('etablissement.phone')">
                  <a-input v-decorator="['phone']" />
                </a-form-item>
                <a-form-item :label="$t('comptabilite.email')">
                  <a-input v-decorator="['email']" />
                </a-form-item>
                <a-form-item :label="$t('etablissement.adress')">
                  <a-input v-decorator="['adress']" />
                </a-form-item>
                <a-form-item :label="$t('fournisseur.website')">
                  <a-input v-decorator="['website']" />
                </a-form-item>
              </a-form>
              <div v-else class="info-list">
                <div class="info-row">
                  <a-icon type="shop" class="info-icon" />
                  <span class="info-label">{{ $t("fournisseur.companyName") }}</span>
                  <span class="info-value">{{ provider.fullName || "—" }}</span>
                </div>
                <div class="info-row">
                  <a-icon type="phone" class="info-icon" />
                  <span class="info-label">{{ $t("etablissement.phone") }}</span>
                  <span class="info-value">{{ provider.phone || "—" }}</span>
                </div>
                <div class="info-row">
                  <a-icon type="mail" class="info-icon" />
                  <span class="info-label">{{ $t("comptabilite.email") }}</span>
                  <span class="info-value">{{ provider.email || "—" }}</span>
                </div>
                <div class="info-row">
                  <a-icon type="environment" class="info-icon" />
                  <span class="info-label">{{ $t("etablissement.adress") }}</span>
                  <span class="info-value">{{ provider.adress || "—" }}</span>
                </div>
                <div class="info-row">
                  <a-icon type="global" class="info-icon" />
                  <span class="info-label">{{ $t("fournisseur.website") }}</span>
                  <span class="info-value">{{ provider.website || "—" }}</span>
                </div>
              </div>
            </div>

            <div class="info-section">
              <h5 class="section-title">
                <a-icon type="bank" /> {{ $t("fournisseur.businessInfo") }}
              </h5>
              <a-form v-if="editingMode" :form="form" layout="vertical" class="compact-form">
                <a-form-item :label="$t('fournisseur.codeTVA')">
                  <a-input v-decorator="['codeTVA']" />
                </a-form-item>
                <a-form-item :label="$t('etablissement.matriculeFiscale')">
                  <a-input v-decorator="['matriculeFiscal']" />
                </a-form-item>
                <a-form-item :label="$t('fournisseur.retenue')">
                  <a-input-number
                    v-decorator="['retenue']"
                    :min="0"
                    :max="100"
                    style="width: 100%"
                    :formatter="(v) => v + '%'"
                    :parser="(v) => v.replace('%', '')"
                  />
                </a-form-item>
              </a-form>
              <div v-else class="info-list">
                <div class="info-row">
                  <a-icon type="barcode" class="info-icon" />
                  <span class="info-label">{{ $t("fournisseur.codeTVA") }}</span>
                  <span class="info-value">{{ provider.codeTVA || "—" }}</span>
                </div>
                <div class="info-row">
                  <a-icon type="idcard" class="info-icon" />
                  <span class="info-label">{{ $t("etablissement.matriculeFiscale") }}</span>
                  <span class="info-value">{{ provider.matriculeFiscal || "—" }}</span>
                </div>
                <div class="info-row">
                  <a-icon type="percentage" class="info-icon" />
                  <span class="info-label">{{ $t("fournisseur.retenue") }}</span>
                  <span class="info-value">{{ (provider.retenue || 0) }}%</span>
                </div>
              </div>
            </div>
          </div>

          <div class="info-section accounting-plan-section">
            <h5 class="section-title">
              <a-icon type="audit" /> {{ $t("comptabilite.planComptable") }}
            </h5>
            <div class="info-list">
              <div class="info-row">
                <a-icon type="tag" class="info-icon" />
                <span class="info-label">{{ $t("comptabilite.accountingClass") }}</span>
                <span class="info-value">
                  <a-tag v-if="provider.accountingClass" color="geekblue">
                    {{ provider.accountingClass }}
                  </a-tag>
                  <span v-else class="text-muted">{{
                    $t("comptabilite.accountingClassNotAssigned")
                  }}</span>
                </span>
              </div>
              <div class="info-row">
                <a-icon type="folder-open" class="info-icon" />
                <span class="info-label">{{ $t("comptabilite.accountingParentClass") }}</span>
                <span class="info-value">{{
                  provider.accountingParentClass || "—"
                }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Invoice table -->
        <div class="card mt-3">
          <div class="card-header d-flex justify-content-between align-items-center">
            <h5 class="mb-0"><a-icon type="file-text" /> {{ $t("fournisseur.invoiceHistory") }}</h5>
            <a-tag>{{ invoices.length }} {{ $t("fournisseur.totalInvoices").toLowerCase() }}</a-tag>
          </div>
          <div class="card-body p-0">
            <invoice-table
              :invoices="invoices"
              :loading="loadingInvoices"
              :show-provider="false"
              @row-click="openScheduleDrawer"
            />
          </div>
        </div>

        <div class="card mt-3">
          <div class="card-header d-flex justify-content-between align-items-center">
            <h5 class="mb-0"><a-icon type="swap" /> {{ $t("fournisseur.creditNotesTitle") }}</h5>
          </div>
          <div class="card-body p-0 fournisseur-credit-notes-table">
            <a-table
              row-key="_id"
              size="small"
              :loading="loadingAvoirs"
              :columns="creditNoteColumns"
              :data-source="creditNotes"
              :pagination="false"
              :custom-row="(r) => ({ on: { click: () => openAvoirDetailDrawer(r) } })"
            >
              <template slot="amount" slot-scope="text">
                <span>{{ text != null ? Number(text).toFixed(3) : "—" }}</span>
              </template>
              <template slot="status" slot-scope="text">
                <a-tag :color="avoirStatusColor(text)">{{ avoirStatusLabel(text) }}</a-tag>
              </template>
              <template slot="action" slot-scope="text, record">
                <div class="avoir-actions-row">
                  <a-tooltip :title="$t('action.consulter')">
                    <a-button
                      type="link"
                      size="small"
                      class="avoir-btn-consult"
                      @click.stop="openAvoirDetailDrawer(record)"
                    >
                      <a-icon type="eye" />
                    </a-button>
                  </a-tooltip>
                  <a-button
                    v-if="record.status === 'pending_reception'"
                    type="primary"
                    size="small"
                    class="avoir-btn-confirm"
                    @click.stop="openAvoirReceive(record)"
                  >{{ $t("avoir.confirmReception") }}</a-button>
                </div>
              </template>
            </a-table>
            <div v-if="!loadingAvoirs && creditNotes.length === 0" class="text-center text-muted py-3 px-3">
              {{ $t("fournisseur.creditNotesEmpty") }}
            </div>
          </div>
        </div>
      </div>

      <!-- Right sidebar: stats + charts -->
      <div class="body-right">
        <!-- Stats cards -->
        <div class="stats-grid">
          <div class="stat-card stat-invoices">
            <div class="stat-icon-wrap stat-icon-blue">
              <a-icon type="file-text" />
            </div>
            <div class="stat-content">
              <span class="stat-number">{{ stats.totalInvoices }}</span>
              <span class="stat-label">{{ $t("fournisseur.totalInvoices") }}</span>
            </div>
          </div>
          <div class="stat-card stat-amount">
            <div class="stat-icon-wrap stat-icon-purple">
              <a-icon type="dollar" />
            </div>
            <div class="stat-content">
              <span class="stat-number">{{ stats.totalAmount.toFixed(3) }}</span>
              <span class="stat-label">{{ $t("fournisseur.totalAmount") }}</span>
            </div>
          </div>
          <div class="stat-card stat-paid">
            <div class="stat-icon-wrap stat-icon-green">
              <a-icon type="check-circle" />
            </div>
            <div class="stat-content">
              <span class="stat-number">{{ stats.totalPaid.toFixed(3) }}</span>
              <span class="stat-label">{{ $t("fournisseur.totalPaid") }}</span>
            </div>
          </div>
          <div class="stat-card stat-remaining">
            <div class="stat-icon-wrap stat-icon-orange">
              <a-icon type="clock-circle" />
            </div>
            <div class="stat-content">
              <span class="stat-number">{{ stats.totalRemaining.toFixed(3) }}</span>
              <span class="stat-label">{{ $t("fournisseur.totalRemaining") }}</span>
            </div>
          </div>
        </div>

        <!-- Payment progress -->
        <div class="card mt-3 chart-card">
          <h5 class="section-title mb-3">
            <a-icon type="rise" /> {{ $t("fournisseur.paymentProgress") }}
          </h5>
          <a-progress
            :percent="paymentPercent"
            :stroke-color="paymentPercent >= 100 ? '#52c41a' : paymentPercent >= 50 ? '#faad14' : '#f5222d'"
            :format="() => paymentPercent + '%'"
            status="active"
          />
          <div class="progress-legend">
            <span class="legend-paid"><span class="legend-dot green" /> {{ $t("fournisseur.paid") }}: {{ stats.totalPaid.toFixed(3) }}</span>
            <span class="legend-remaining"><span class="legend-dot orange" /> {{ $t("fournisseur.remaining") }}: {{ stats.totalRemaining.toFixed(3) }}</span>
          </div>
        </div>

        <!-- Doughnut chart -->
        <div class="card mt-3 chart-card">
          <h5 class="section-title mb-3">
            <a-icon type="pie-chart" /> {{ $t("fournisseur.paymentBreakdown") }}
          </h5>
          <div v-if="stats.totalAmount > 0" class="chart-wrap">
            <payment-doughnut :chartdata="doughnutData" :options="doughnutOptions" />
          </div>
          <div v-else class="text-center text-muted py-4">
            {{ $t("fournisseur.noData") }}
          </div>
        </div>

        <!-- Invoice status breakdown -->
        <div class="card mt-3 chart-card">
          <h5 class="section-title mb-3">
            <a-icon type="bar-chart" /> {{ $t("fournisseur.invoiceStatus") }}
          </h5>
          <div v-if="invoices.length" class="status-bars">
            <div class="status-bar-row">
              <span class="status-label"><a-badge color="green" /> {{ $t("fournisseur.paid") }}</span>
              <a-progress :percent="paidPercent" :show-info="false" stroke-color="#52c41a" size="small" />
              <span class="status-count">{{ paidCount }}</span>
            </div>
            <div class="status-bar-row">
              <span class="status-label"><a-badge color="orange" /> {{ $t("fournisseur.partial") }}</span>
              <a-progress :percent="partialPercent" :show-info="false" stroke-color="#faad14" size="small" />
              <span class="status-count">{{ partialCount }}</span>
            </div>
            <div class="status-bar-row">
              <span class="status-label"><a-badge color="red" /> {{ $t("fournisseur.unpaid") }}</span>
              <a-progress :percent="unpaidPercent" :show-info="false" stroke-color="#f5222d" size="small" />
              <span class="status-count">{{ unpaidCount }}</span>
            </div>
          </div>
          <div v-else class="text-center text-muted py-3">
            {{ $t("fournisseur.noData") }}
          </div>
        </div>
      </div>
    </div>

    <schedule-drawer
      :visible="drawerVisible"
      :depense="selectedDepense"
      @close="drawerVisible = false"
      @refresh="loadInvoices"
    />

    <avoir-receive-modal
      :visible="receiveAvoirVisible"
      :confirming="receivingAvoir"
      :total-amount="receiveAvoirAmount"
      :lookups="avoirLookups"
      @cancel="receiveAvoirVisible = false"
      @confirm="onAvoirReceiveConfirm"
    />

    <avoir-detail-drawer
      :visible="avoirDetailDrawerVisible"
      :avoir-id="selectedAvoirId"
      @close="onAvoirDetailDrawerClose"
    />
  </div>
</template>

<script>
import apiClient from '@/services/axios'
import {
  fetchSupplierProfile,
  fetchSupplierInvoices,
} from '@/services/api/compV3/suppliers'
import {
  fetchCreditNotes,
  receiveCreditNote,
} from '@/services/api/compV3/paymentEntry'
import { fetchDecaissementLookups } from '@/services/api/compV3/decaissement'
import { showApiError } from '@/utilities/showApiError'
import InvoiceTable from './components/InvoiceTable.vue'
import ScheduleDrawer from './components/ScheduleDrawer.vue'
import PaymentDoughnut from './components/PaymentDoughnut.vue'
import AvoirReceiveModal from '../avoir/components/AvoirReceiveModal.vue'
import AvoirDetailDrawer from '../avoir/components/AvoirDetailDrawer.vue'

export default {
  name: 'SupplierProfilePage',
  components: { InvoiceTable, ScheduleDrawer, PaymentDoughnut, AvoirReceiveModal, AvoirDetailDrawer },
  beforeMount() {
    this.form = this.$form.createForm(this)
  },
  data() {
    return {
      provider: {},
      stats: { totalInvoices: 0, totalAmount: 0, totalPaid: 0, totalRemaining: 0 },
      providersList: [],
      invoices: [],
      loadingInvoices: false,
      editingMode: false,
      loadingSave: false,
      drawerVisible: false,
      selectedDepense: null,
      creditNotes: [],
      loadingAvoirs: false,
      avoirLookups: { myCaisse: null, otherCaisses: [], caisseBanks: [] },
      receiveAvoirVisible: false,
      receiveAvoirAmount: 0,
      receiveAvoirId: null,
      receivingAvoir: false,
      avoirDetailDrawerVisible: false,
      selectedAvoirId: null,
    }
  },
  computed: {
    creditNoteColumns() {
      return [
        { title: this.$t('bulletin.label'), dataIndex: 'label', key: 'label' },
        { title: this.$t('recette.montant'), dataIndex: 'amount', key: 'amount', scopedSlots: { customRender: 'amount' } },
        { title: this.$t('action.status'), dataIndex: 'status', key: 'status', scopedSlots: { customRender: 'status' } },
        { title: this.$t('action.actions'), key: 'action', scopedSlots: { customRender: 'action' }, align: 'center' },
      ]
    },
    paymentPercent() {
      if (!this.stats.totalAmount) return 0
      return Math.min(100, Math.round((this.stats.totalPaid / this.stats.totalAmount) * 100))
    },
    paidCount() {
      return this.invoices.filter((i) => i.invoiceStatus === 'paid').length
    },
    partialCount() {
      return this.invoices.filter((i) => i.invoiceStatus === 'partial').length
    },
    unpaidCount() {
      return this.invoices.filter((i) => i.invoiceStatus === 'unpaid' || !i.invoiceStatus).length
    },
    paidPercent() {
      return this.invoices.length ? Math.round((this.paidCount / this.invoices.length) * 100) : 0
    },
    partialPercent() {
      return this.invoices.length ? Math.round((this.partialCount / this.invoices.length) * 100) : 0
    },
    unpaidPercent() {
      return this.invoices.length ? Math.round((this.unpaidCount / this.invoices.length) * 100) : 0
    },
    doughnutData() {
      return {
        labels: [this.$t('fournisseur.paid'), this.$t('fournisseur.remaining')],
        datasets: [
          {
            data: [this.stats.totalPaid, this.stats.totalRemaining],
            backgroundColor: ['#52c41a', '#faad14'],
            borderWidth: 0,
            hoverBackgroundColor: ['#389e0d', '#d48806'],
          },
        ],
      }
    },
    doughnutOptions() {
      return {
        responsive: true,
        maintainAspectRatio: true,
        cutoutPercentage: 65,
        legend: {
          position: 'bottom',
          labels: { boxWidth: 12, padding: 16 },
        },
        tooltips: {
          callbacks: {
            label(item, data) {
              const val = data.datasets[0].data[item.index]
              return ` ${data.labels[item.index]}: ${Number(val).toFixed(3)}`
            },
          },
        },
      }
    },
  },
  async created() {
    await this.loadProvidersList()
    await this.loadProfile()
    await this.loadInvoices()
    await this.loadAvoirLookups()
    await this.loadCreditNotes()
  },
  methods: {
    async loadProvidersList() {
      try {
        const res = await apiClient.post('/providers/filter', { query: { status: 'active' } })
        this.providersList = res.data || []
      } catch (_) { /* silent */ }
    },
    async loadProfile() {
      try {
        const id = this.$route.params.providerId
        const res = await fetchSupplierProfile(id)
        this.provider = res.data.provider || {}
        this.stats = res.data.stats || this.stats
      } catch (e) {
        showApiError(this, e)
        this.$router.push('/decaissement/fournisseurs')
      }
    },
    async loadInvoices() {
      this.loadingInvoices = true
      try {
        const id = this.$route.params.providerId
        const res = await fetchSupplierInvoices(id)
        this.invoices = res.data || []
      } catch (e) {
        showApiError(this, e)
      } finally {
        this.loadingInvoices = false
      }
    },
    async loadAvoirLookups() {
      try {
        const lookupsRes = await fetchDecaissementLookups()
        const ps = lookupsRes.data?.paymentSources || {}
        this.avoirLookups = {
          myCaisses: ps.myCaisses || (ps.myCaisse ? [ps.myCaisse] : []),
          otherCaisses: ps.otherCaisses || [],
          caisseBanks: ps.caisseBanks || [],
        }
      } catch (_) { /* silent */ }
    },
    async loadCreditNotes() {
      this.loadingAvoirs = true
      try {
        const id = this.$route.params.providerId
        const res = await fetchCreditNotes({ provider: id, pageSize: 100, page: 1 })
        const body = res.data || {}
        this.creditNotes = body.data || []
      } catch (e) {
        showApiError(this, e)
      } finally {
        this.loadingAvoirs = false
      }
    },
    avoirStatusColor(s) {
      if (s === 'received') return 'green'
      if (s === 'cancelled') return 'default'
      return 'orange'
    },
    avoirStatusLabel(s) {
      if (s === 'received') return this.$t('avoir.received')
      if (s === 'cancelled') return this.$t('avoir.cancelled')
      return this.$t('avoir.pendingReception')
    },
    openAvoirReceive(record) {
      this.receiveAvoirId = record._id
      this.receiveAvoirAmount = Number(record.amount) || 0
      this.receiveAvoirVisible = true
    },
    async onAvoirReceiveConfirm({ splits, receiptNumber }) {
      this.receivingAvoir = true
      try {
        await receiveCreditNote(this.receiveAvoirId, { splits, receiptNumber })
        this.$message.success(this.$t('avoir.receptionConfirmed'))
        this.receiveAvoirVisible = false
        await this.loadCreditNotes()
      } catch (e) {
        showApiError(this, e)
      } finally {
        this.receivingAvoir = false
      }
    },
    openAvoirDetailDrawer(record) {
      const id = record._id != null ? String(record._id) : ''
      if (!id) return
      this.selectedAvoirId = id
      this.avoirDetailDrawerVisible = true
    },
    onAvoirDetailDrawerClose() {
      this.avoirDetailDrawerVisible = false
      this.selectedAvoirId = null
    },
    handleProviderChange(val) {
      this.$router.push({ path: `/decaissement/fournisseur/${val}` })
      this.$nextTick(async () => {
        await this.loadProfile()
        await this.loadInvoices()
        await this.loadCreditNotes()
      })
    },
    setEditingMode() {
      this.editingMode = true
      this.$nextTick(() => {
        this.form.setFieldsValue({
          fullName: this.provider.fullName || '',
          codeTVA: this.provider.codeTVA || '',
          phone: this.provider.phone || '',
          retenue: this.provider.retenue || 0,
          adress: this.provider.adress || '',
          email: this.provider.email || '',
          website: this.provider.website || '',
          matriculeFiscal: this.provider.matriculeFiscal || '',
        })
      })
    },
    saveProvider() {
      this.form.validateFields((err, values) => {
        if (err) return
        this.loadingSave = true
        apiClient
          .patch(`/providers/${this.provider._id}`, { data: values })
          .then(() => {
            this.$message.success(this.$t('success.fournisseurMSA'))
            this.editingMode = false
            this.loadProfile()
          })
          .catch((e) => showApiError(this, e))
          .finally(() => {
            this.loadingSave = false
          })
      })
    },
    openScheduleDrawer(depense) {
      this.selectedDepense = depense
      this.drawerVisible = true
    },
  },
}
</script>

<style scoped>
.supplier-profile {
  max-width: 1600px;
  margin: 0 auto;
}

/* Header */
.profile-header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 16px;
  margin-bottom: 16px;
  background: #fff;
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  white-space: nowrap;
  overflow: hidden;
}
.back-btn {
  padding: 0 4px;
  font-size: 16px;
  color: #595959;
  flex-shrink: 0;
}
.back-btn:hover { color: #1890ff; }
.header-divider {
  width: 1px;
  height: 22px;
  background: #e8e8e8;
  flex-shrink: 0;
}
.provider-name {
  margin: 0;
  font-size: 15px;
  font-weight: 600;
  color: #262626;
  overflow: hidden;
  text-overflow: ellipsis;
  flex-shrink: 1;
  min-width: 0;
}
.status-tag { flex-shrink: 0; }
.header-spacer { flex: 1; min-width: 4px; }

/* Body layout */
.profile-body {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 340px;
  gap: 16px;
  align-items: start;
}
.body-right {
  min-width: 0;
  overflow: hidden;
}
@media (max-width: 1200px) {
  .profile-body {
    grid-template-columns: 1fr;
  }
}

/* Cards */
.card {
  background: #fff;
  border: 1px solid #f0f0f0;
  border-radius: 10px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}
.card-header {
  padding: 16px 20px;
  border-bottom: 1px solid #f0f0f0;
}
.card-body { padding: 20px; }
.card-body.p-0 { padding: 0; }

/* Info card */
.info-card { padding: 20px; }
.info-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
}
@media (max-width: 768px) {
  .info-grid { grid-template-columns: 1fr; }
}
.section-title {
  font-size: 15px;
  font-weight: 600;
  color: #262626;
  margin-bottom: 16px;
}
.section-title i {
  color: #1890ff;
  margin-right: 6px;
}
.info-list { display: flex; flex-direction: column; gap: 12px; }
.info-row {
  display: grid;
  grid-template-columns: 20px 130px 1fr;
  gap: 8px;
  align-items: center;
  font-size: 13.5px;
}
.info-icon { color: #8c8c8c; font-size: 15px; }
.info-label { color: #8c8c8c; font-weight: 500; }
.info-value { color: #262626; font-weight: 500; }
.accounting-plan-section {
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid #f0f0f0;
}
.compact-form .ant-form-item { margin-bottom: 12px; }

/* Stats grid */
.stats-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}
.stat-card {
  background: #fff;
  border: 1px solid #f0f0f0;
  border-radius: 10px;
  padding: 12px;
  display: flex;
  align-items: center;
  gap: 10px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  min-width: 0;
  overflow: hidden;
}
.stat-icon-wrap {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  flex-shrink: 0;
}
.stat-icon-blue { background: #e6f7ff; color: #1890ff; }
.stat-icon-purple { background: #f9f0ff; color: #722ed1; }
.stat-icon-green { background: #f6ffed; color: #52c41a; }
.stat-icon-orange { background: #fff7e6; color: #fa8c16; }
.stat-content {
  display: flex;
  flex-direction: column;
  min-width: 0;
  overflow: hidden;
}
.stat-number {
  font-size: 16px;
  font-weight: 700;
  color: #262626;
  line-height: 1.2;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.stat-label {
  font-size: 11px;
  color: #8c8c8c;
  margin-top: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Charts */
.chart-card { padding: 16px; }
.chart-wrap {
  max-width: 220px;
  margin: 0 auto;
}
.progress-legend {
  display: flex;
  justify-content: space-between;
  margin-top: 12px;
  font-size: 12.5px;
  color: #595959;
}
.legend-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-right: 4px;
}
.legend-dot.green { background: #52c41a; }
.legend-dot.orange { background: #faad14; }

/* Status bars */
.status-bars { display: flex; flex-direction: column; gap: 14px; }
.status-bar-row {
  display: grid;
  grid-template-columns: 110px 1fr 30px;
  align-items: center;
  gap: 10px;
}
.status-label { font-size: 13px; color: #595959; }
.status-count {
  font-size: 13px;
  font-weight: 600;
  color: #262626;
  text-align: right;
}

.fournisseur-credit-notes-table ::v-deep .ant-table-tbody > tr {
  cursor: pointer;
}
.avoir-actions-row {
  display: inline-flex;
  align-items: center;
  flex-wrap: nowrap;
  gap: 6px;
  white-space: nowrap;
  justify-content: center;
}
.avoir-btn-consult {
  padding: 0 4px;
  min-width: auto;
  line-height: 1;
}
.avoir-btn-confirm {
  flex-shrink: 0;
}
</style>

<style>
@import "~@/views/compV3/common.css";
</style>
